import { useEffect } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { API_BASE_URL } from "@/lib/api"
import { getSseToken } from "@/lib/api/notifications"

/**
 * Subscribe to SSE notification stream; invalidates notifications query on new events.
 */
export function useNotificationSSE(enabled: boolean) {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!enabled || typeof window === "undefined" || typeof EventSource === "undefined") {
      return
    }

    let source: EventSource | null = null
    let onNew: (() => void) | null = null
    let cancelled = false

    void (async () => {
      try {
        const { token } = await getSseToken()
        if (cancelled || !token) return

        const url = `${API_BASE_URL}/notifications/sse?token=${encodeURIComponent(token)}`
        source = new EventSource(url, { withCredentials: true })

        onNew = () => {
          queryClient.invalidateQueries({ queryKey: ["notifications"] })
        }

        source.addEventListener("notification_new", onNew)
      } catch {
        // SSE is best-effort; polling still works via React Query
      }
    })()

    return () => {
      cancelled = true
      if (source && onNew) {
        source.removeEventListener("notification_new", onNew)
      }
      source?.close()
    }
  }, [enabled, queryClient])
}
